/**
 * Verified data boundary for the public landing hero's example forecast.
 *
 * The landing surface must only receive a verified ForestModeler plan result
 * for this property. The historical Arvometsä document is retained in the
 * provenance note as research, but is deliberately not exposed to the UI.
 */

export const LANDING_HERO_EXAMPLE_PROPERTY_ID = '178-415-5-2';

export interface LandingHeroForecastPoint {
  /** Calendar year at the end of the reported forecast interval. */
  year: number;
  value: number;
}

export interface LandingHeroForecastSeries {
  /** Display unit exactly as reported by the source plan. */
  unit: string;
  points: readonly [LandingHeroForecastPoint, ...LandingHeroForecastPoint[]];
}

export interface LandingHeroForecastProvenance {
  sourcePlanLabel: string;
  sourcePlanDate: string;
  sourceModel: 'pukkala';
  timeBasis: 'calendar-year endpoints, 2026–2051';
  propertyId: typeof LANDING_HERO_EXAMPLE_PROPERTY_ID;
  sourcePath: string;
  growthLocator: string;
  harvestIncomeLocator: string;
  alternativesLocator: string;
  valuationBasis: 'active-snapshot-reranking-over-persisted-branches';
  /** Model results are forecasts; none of the values are presented as observations. */
  valueClass: 'modelled-example-plan';
}

export interface LandingHeroForecastReady {
  status: 'ready';
  propertyId: typeof LANDING_HERO_EXAMPLE_PROPERTY_ID;
  sourcePlanLabel: string;
  scopeLabel: string;
  coverageStandCount: 32;
  propertyStandCount: 32;
  areaHectares: 41.59;
  provenance: LandingHeroForecastProvenance;
  /** Property standing volume at modelled period endpoints, in m³. */
  growth: LandingHeroForecastSeries;
  /** Gross harvest revenue at each model timestep, in EUR. */
  harvestIncome: LandingHeroForecastSeries;
  /** Existing branches reranked by the same active-snapshot basis as Optimize. */
  alternatives: readonly LandingHeroForecastAlternative[];
}

export interface LandingHeroForecastAlternative {
  id: 'whole-period-value' | 'near-term-revenue' | 'avoid-clearcuts' | 'tapio';
  status: 'ready' | 'unavailable';
  firstOperationYear: number | null;
  npvPerHectareEur: number | null;
  netCashEur: number | null;
  unavailableReason?: 'revenue_now_signal_unavailable' | 'saved_plan_comparison_source_unavailable';
}

export interface LandingHeroForecastUnavailable {
  status: 'unavailable';
  propertyId: string;
  /** A public example is available only when this property has a verified served plan. */
  reason: 'no-verified-example-plan-for-property';
}

export type LandingHeroForecastData = LandingHeroForecastReady | LandingHeroForecastUnavailable;

/**
 * This snapshot is copied from the saved-plan and branch records identified by
 * the locators below. It does not run a simulation in the public landing page.
 */
export function landingHeroForecastForProperty(propertyId: string): LandingHeroForecastData {
  if (propertyId === LANDING_HERO_EXAMPLE_PROPERTY_ID) {
    return {
      status: 'ready',
      propertyId: LANDING_HERO_EXAMPLE_PROPERTY_ID,
      sourcePlanLabel: 'Recommended Plan',
      scopeLabel: '32/32 · 41.59 ha',
      coverageStandCount: 32,
      propertyStandCount: 32,
      areaHectares: 41.59,
      provenance: {
        sourcePlanLabel: 'Recommended Plan',
        sourcePlanDate: '2026-08-26',
        sourceModel: 'pukkala',
        timeBasis: 'calendar-year endpoints, 2026–2051',
        propertyId: LANDING_HERO_EXAMPLE_PROPERTY_ID,
        sourcePath:
          'saved plan 7dcde818-f13d-4ab8-a538-c84800025382 / batch 2ce8aa6f-3db4-4c23-ae70-c7bb6b004687 / 32 stands',
        growthLocator: 'terminal branch timesteps × persisted stand area',
        harvestIncomeLocator: 'timestep removed_revenue_by_assortment_eur × persisted stand area',
        alternativesLocator:
          'Optimize plan-generation endpoint with active snapshot 312fc3fa-a7f0-4f98-baca-5e9cf33b521f',
        valuationBasis: 'active-snapshot-reranking-over-persisted-branches',
        valueClass: 'modelled-example-plan',
      },
      growth: {
        unit: 'm³',
        points: [
          { year: 2026, value: 4602.32 },
          { year: 2031, value: 5917.25 },
          { year: 2036, value: 7338.15 },
          { year: 2041, value: 5661.9 },
          { year: 2046, value: 7580.69 },
          { year: 2051, value: 9317.99 },
        ],
      },
      harvestIncome: {
        unit: '€',
        points: [
          { year: 2026, value: 155495.12 },
          { year: 2031, value: 0 },
          { year: 2036, value: 0 },
          { year: 2041, value: 190278.55 },
          { year: 2046, value: 0 },
          { year: 2051, value: 0 },
        ],
      },
      alternatives: [
        {
          id: 'whole-period-value',
          status: 'ready',
          firstOperationYear: 2026,
          npvPerHectareEur: 9912.04,
          netCashEur: 322780.12,
        },
        {
          id: 'avoid-clearcuts',
          status: 'ready',
          firstOperationYear: 2026,
          npvPerHectareEur: 9894.87,
          netCashEur: 322275.51,
        },
        {
          id: 'near-term-revenue',
          status: 'ready',
          firstOperationYear: 2026,
          npvPerHectareEur: 9713.09,
          netCashEur: 407345.44,
        },
        {
          id: 'tapio',
          status: 'ready',
          firstOperationYear: 2026,
          npvPerHectareEur: 8477.62,
          netCashEur: 164791.95,
        },
      ],
    };
  }

  return {
    status: 'unavailable',
    propertyId,
    reason: 'no-verified-example-plan-for-property',
  };
}
